import React, { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, CheckCircle2, MinusCircle, Info } from "lucide-react";
import { api } from "../lib/api";

const FIELD_LABELS = {
  order_id: "Order ID", order_date: "Order Date", product_name: "Product Name",
  sku: "SKU", quantity: "Quantity", unit_price: "Unit Price", total: "Total",
  status: "Order Status", customer_name: "Customer Name",
  customer_email: "Customer Email", phone: "Phone", address: "Address",
  city: "City", state: "State", country: "Country", zip: "Zip",
  category: "Category", discount: "Discount", tax: "Tax", shipping: "Shipping",
  marketplace: "Marketplace", payment_method: "Payment Method",
};

const STATE_META = {
  available:   { Icon: CheckCircle2, color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", label: "Available" },
  partial:     { Icon: AlertTriangle, color: "text-amber-400 bg-amber-500/10 border-amber-500/30",   label: "Partial" },
  unavailable: { Icon: MinusCircle,  color: "text-zinc-500 bg-zinc-700/20 border-zinc-700",         label: "Unavailable" },
};

export default function DataQuality() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/data-quality");
        setReport(data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !report) {
    return <div className="text-sm text-zinc-500">Analyzing your data…</div>;
  }

  const fields = Object.entries(report.capability);
  const available = fields.filter(([, v]) => v === "available");
  const partial = fields.filter(([, v]) => v === "partial");
  const unavailable = fields.filter(([, v]) => v === "unavailable");
  const scoreColor = report.score >= 75 ? "text-emerald-400" : report.score >= 45 ? "text-amber-400" : "text-red-400";

  return (
    <div className="max-w-6xl mx-auto" data-testid="data-quality-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">Data Quality</p>
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
        What your data can power
      </h1>
      <p className="mt-2 text-zinc-400 max-w-2xl">
        Milkath adapts to whatever your sources provide. This page shows the fields
        we have, the ones we don't, and which AI features each unlocks.
      </p>

      {/* Score hero */}
      <div className="mt-10 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 flex flex-wrap items-center gap-6" data-testid="dq-score-card">
        <div className="h-20 w-20 rounded-2xl bg-zinc-950 border border-zinc-800 flex items-center justify-center">
          <ShieldCheck className={`h-8 w-8 ${scoreColor}`} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">Overall data quality score</p>
          <p className={`text-4xl font-semibold tracking-tight ${scoreColor}`} style={{ fontFamily: "var(--font-heading)" }} data-testid="dq-score-value">
            {report.score}<span className="text-zinc-600 text-2xl">/100</span>
          </p>
          <p className="mt-1 text-sm text-zinc-400">
            {available.length} fields available · {partial.length} partial · {unavailable.length} unavailable
          </p>
        </div>
        <div className="flex gap-3 text-xs">
          <Stat label="Duplicate products" value={report.totals.duplicate_products} />
          <Stat label="Duplicate orders" value={report.totals.duplicate_orders} />
          <Stat label="Duplicate customers" value={report.totals.duplicate_customers} />
          <Stat label="Missing categories" value={report.totals.missing_categories} />
        </div>
      </div>

      {/* Capability profile */}
      <h2 className="mt-12 text-lg font-medium text-zinc-100" style={{ fontFamily: "var(--font-heading)" }}>Field availability</h2>
      <p className="text-sm text-zinc-500 mt-1">Each field powers different Milkath features. Available = ready to use.</p>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {fields.map(([field, state]) => {
          const meta = STATE_META[state] || STATE_META.unavailable;
          const Icon = meta.Icon;
          return (
            <div key={field} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-zinc-950/40 ${meta.color}`} data-testid={`dq-field-${field}`}>
              <Icon className="h-4 w-4" />
              <span className="text-sm text-zinc-100 flex-1">{FIELD_LABELS[field] || field}</span>
              <span className="text-[10px] uppercase tracking-[0.16em] font-bold">{meta.label}</span>
            </div>
          );
        })}
      </div>

      {/* Unknown statuses */}
      {report.unknown_statuses.length > 0 && (
        <div className="mt-12">
          <h2 className="text-lg font-medium text-zinc-100" style={{ fontFamily: "var(--font-heading)" }}>Unknown status values</h2>
          <p className="text-sm text-zinc-500 mt-1">These status labels aren't part of Milkath's canonical set and may affect reporting.</p>
          <div className="mt-4 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 flex flex-wrap gap-2" data-testid="dq-unknown-statuses">
            {report.unknown_statuses.map((s, i) => (
              <span key={i} className="px-3 py-1 text-xs rounded-md bg-zinc-800 text-zinc-200 border border-zinc-700">
                {s.status} <span className="text-zinc-500 ml-1">({s.count})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Warnings */}
      {report.warnings.length > 0 && (
        <div className="mt-12">
          <h2 className="text-lg font-medium text-zinc-100" style={{ fontFamily: "var(--font-heading)" }}>Recent import notices</h2>
          <div className="mt-4 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
            <ul className="space-y-2">
              {report.warnings.map((w, i) => (
                <li key={i} className="text-sm text-zinc-400 flex items-start gap-2" data-testid={`dq-warning-${i}`}>
                  <Info className="h-3.5 w-3.5 mt-0.5 text-zinc-600" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 px-3 py-2 min-w-[110px]">
      <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">{label}</p>
      <p className="mt-1 text-base font-semibold tabular-nums text-zinc-100">{value}</p>
    </div>
  );
}
